import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images, Play } from "lucide-react";

const PROJECTS = [
  ["/gallery/ksu-01.jpg", "/gallery/ksu-02.jpg", "/gallery/ksu-03.jpg", "/gallery/ksu-04.jpg", "/gallery/ksu-06.jpg", "/gallery/ksu-08.jpg"],
  [
    { type: "video", src: "/gallery/paris-video.mp4", webm: "/gallery/paris-video.webm" },
    "/gallery/omicron-01.jpg", "/gallery/smc-01.jpg", "/gallery/arcadis-01.jpg", "/gallery/rte-01.jpg", "/gallery/ctc-01.jpg", "/gallery/rtds-01.jpg", "/gallery/coel-01.jpg",
  ],
  [
    { type: "video", src: "/gallery/bern-public-video.mp4", webm: "/gallery/bern-public-video.webm", poster: "/gallery/bern-public-cover.jpg" },
  ],
  [
    { type: "video", src: "/gallery/lenzburg-video.mp4", webm: "/gallery/lenzburg-video.webm", poster: "/gallery/lenzburg-cover.jpg" },
  ],
];

const mediaSrc = (m) => (typeof m === "string" ? m : m.src);
const isVideo = (m) => typeof m !== "string" && m.type === "video";
const coverOf = (arr) => {
  const img = arr.find((m) => !isVideo(m));
  return img ? mediaSrc(img) : arr[0].poster;
};

export const Gallery = ({ t }) => {
  const [view, setView] = useState(null); // { p: projectIndex, i: imageIndex }

  const close = useCallback(() => setView(null), []);
  const prev = useCallback(() => setView((v) => ({ p: v.p, i: (v.i - 1 + PROJECTS[v.p].length) % PROJECTS[v.p].length })), []);
  const next = useCallback(() => setView((v) => ({ p: v.p, i: (v.i + 1) % PROJECTS[v.p].length })), []);

  useEffect(() => {
    if (!view) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, close, prev, next]);

  return (
    <section id="projekte" data-testid="gallery-section" className="section-pad bg-[#0b0b0e] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <p data-testid="gallery-overline" className="font-mono-accent text-xs uppercase tracking-[0.3em] text-pink-brand mb-4">
            {t.gallery.overline}
          </p>
          <h2 data-testid="gallery-title" className="font-display font-bold text-4xl md:text-5xl text-white tracking-tight">
            {t.gallery.title}
          </h2>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-neutral-400">{t.gallery.sub}</p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
          {t.gallery.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
              data-testid={`gallery-item-${i}`}
              onClick={() => setView({ p: i, i: 0 })}
              className="group relative overflow-hidden aspect-[4/3] border border-white/10 cursor-pointer"
            >
              <img
                src={coverOf(PROJECTS[i])}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {isVideo(PROJECTS[i][0]) && (
                <span data-testid={`gallery-play-${i}`} className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 bg-pink-brand/90 flex items-center justify-center">
                    <Play size={22} className="text-white ml-0.5" fill="white" />
                  </span>
                </span>
              )}
              {PROJECTS[i].length > 1 && (
                <span data-testid={`gallery-count-${i}`} className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-black/70 backdrop-blur px-2.5 py-1 font-mono-accent text-[10px] tracking-widest text-white">
                  <Images size={12} />
                  {PROJECTS[i].length}
                </span>
              )}
              <div className="absolute inset-0 bg-[#08080a]/70 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-pink-brand">{item.tag}</p>
                <h3 className="mt-2 font-display text-xl md:text-2xl font-semibold text-white">{item.title}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {view && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            onClick={close}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          >
            <button
              data-testid="lightbox-close-button"
              onClick={close}
              className="absolute top-4 right-4 md:top-8 md:right-8 text-white/70 hover:text-pink-brand transition-colors duration-200 p-2"
              aria-label="Close"
            >
              <X size={28} />
            </button>

            {PROJECTS[view.p].length > 1 && (
              <button
                data-testid="lightbox-prev-button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-pink-brand transition-colors duration-200 p-2"
                aria-label="Previous"
              >
                <ChevronLeft size={40} />
              </button>
            )}

            {isVideo(PROJECTS[view.p][view.i]) ? (
              <motion.video
                key={`${view.p}-${view.i}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                data-testid="lightbox-video"
                controls
                playsInline
                poster={PROJECTS[view.p][view.i].poster}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-[80vh] border border-white/10"
              >
                <source src={PROJECTS[view.p][view.i].src} type="video/mp4" />
                <source src={PROJECTS[view.p][view.i].webm} type="video/webm" />
              </motion.video>
            ) : (
              <motion.img
                key={`${view.p}-${view.i}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                data-testid="lightbox-image"
                src={mediaSrc(PROJECTS[view.p][view.i])}
                alt={t.gallery.items[view.p].title}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-[80vh] object-contain border border-white/10"
              />
            )}

            {PROJECTS[view.p].length > 1 && (
              <button
                data-testid="lightbox-next-button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-pink-brand transition-colors duration-200 p-2"
                aria-label="Next"
              >
                <ChevronRight size={40} />
              </button>
            )}

            <div data-testid="lightbox-caption" className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 text-center">
              <p className="font-display text-lg font-semibold text-white">{t.gallery.items[view.p].title}</p>
              <p className="mt-1 font-mono-accent text-xs text-neutral-400">{view.i + 1} / {PROJECTS[view.p].length}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
